// ─── Récapitulatif d'un poste : totaux, parts, qui doit à qui ─────
// Lecture seule sauf le bouton « Rembourser », qui crée l'opération de
// transfert rééquilibrant les soldes.
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI, useT } from '@mop/core';
import { formatEuro, recapBudget, soldesPersonnes, calculerDettes } from './services/calculs';

export default function RecapBudget({ operations = [], payeurs = [], onRembourser }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const recap = useMemo(() => recapBudget(operations, payeurs), [operations, payeurs]);
  const dettes = useMemo(
    () => calculerDettes(soldesPersonnes(operations, payeurs)),
    [operations, payeurs],
  );
  const couleurResultat = recap.resultat > 0 ? (UI.ok || '#2e7d32') : recap.resultat < 0 ? (UI.danger || '#c0392b') : UI.ink;

  return (
    <View style={s.carte}>
      <View style={s.totaux}>
        <View style={s.bloc}>
          <Text style={s.blocLbl}>{t('budget.totalPartage')}</Text>
          <Text style={s.blocVal}>{formatEuro(recap.total)}</Text>
        </View>
        <View style={s.bloc}>
          <Text style={s.blocLbl}>{t('budget.revenus')}</Text>
          <Text style={s.blocVal}>{formatEuro(recap.totalRevenus)}</Text>
        </View>
        <View style={s.bloc}>
          <Text style={s.blocLbl}>{t('budget.resultat')}</Text>
          <Text style={[s.blocVal, { color: couleurResultat }]}>{formatEuro(recap.resultat)}</Text>
        </View>
      </View>

      {recap.lignes.map(l => (
        <View key={l.uid} style={s.ligne}>
          <Text style={s.nom} numberOfLines={1}>
            {l.autres ? t('budget.autresPayeurs') : l.nom}{!l.autres && l.pct ? `  ·  ${l.pct} %` : ''}
          </Text>
          <Text style={s.detail}>{t('budget.payeDu', { paye: formatEuro(l.paye), du: formatEuro(l.partDue) })}</Text>
          <Text style={[s.solde, { color: l.solde >= 0 ? (UI.ok || '#2e7d32') : (UI.danger || '#c0392b') }]}>
            {formatEuro(l.solde)}
          </Text>
        </View>
      ))}

      {dettes.length > 0 && (
        <>
          <Text style={s.titre}>{t('budget.quiDoitAQui')}</Text>
          {dettes.map((d, i) => (
            <View key={i} style={s.dette}>
              <Text style={s.detteTxt} numberOfLines={1}>
                <Text style={{ fontWeight: '800' }}>{d.de}</Text>
                {'  →  '}
                <Text style={{ fontWeight: '800' }}>{d.a}</Text>
                {'   '}{formatEuro(d.montant)}
              </Text>
              {!!onRembourser && (
                <TouchableOpacity style={s.btn} activeOpacity={0.7} onPress={() => onRembourser(d)}>
                  <Text style={s.btnTxt}>↩️ {t('budget.rembourser')}</Text>
                </TouchableOpacity>
              )}
            </View>
          ))}
        </>
      )}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  carte: { backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginBottom: 12 },
  totaux: { flexDirection: 'row', gap: 10 },
  bloc: { flex: 1 },
  blocLbl: { fontSize: 11, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  blocVal: { fontSize: 15, fontWeight: '800', color: UI.ink, marginTop: 2 },
  ligne: { marginTop: 11, paddingTop: 9, borderTopWidth: 0.5, borderTopColor: UI.border },
  nom: { fontSize: 14, fontWeight: '700', color: UI.ink },
  detail: { fontSize: 12, color: UI.muted, marginTop: 1 },
  solde: { fontSize: 14.5, fontWeight: '800', marginTop: 2 },
  titre: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 14, marginBottom: 4 },
  dette: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 7 },
  detteTxt: { flex: 1, fontSize: 13.5, color: UI.ink },
  btn: { paddingVertical: 7, paddingHorizontal: 11, borderRadius: 9, borderWidth: 0.5, borderColor: UI.accent },
  btnTxt: { fontSize: 12.5, color: UI.accent, fontWeight: '700' },
});
